// src/services/toolboxService.ts
// Fetches live toolbox talks from the Cloudflare Worker API (Neon database).
// Maps to the ToolboxTalk shape Toolbox.tsx expects.
//
// Architecture: seed-from-API (same as timesheetsService).
// Toolbox.tsx uses useState(INITIAL_TALKS) and addsTalks locally when
// a new talk is conducted. On first load, Neon talks are merged in.
// IDs never clash (Neon UUIDs vs local 'tb1', 'tb2' strings).

import { api } from '../utils/api'

interface ApiAttendee {
  id:          string
  toolboxId?:  string
  toolbox_id?: string
  userId?:     string | null
  user_id?:    string | null
  workerName?: string
  worker_name?: string
  signature?:  string | null
  signedAt?:   string | null
  signed_at?:  string | null
}

interface ApiToolboxTalk {
  id:             string
  jobId?:         string
  job_id?:        string
  swmsId?:        string | null
  swms_id?:       string | null
  topic?:         string
  notes?:         string | null
  conductedById?: string | null
  conducted_by_id?: string | null
  conductedAt?:   string | null
  conducted_at?:  string | null
  createdAt?:     string
  created_at?:    string
  attendees?:     ApiAttendee[]
}

// What Toolbox.tsx expects — matches the local ToolboxTalk interface
export interface MappedToolboxTalk {
  id:              string
  talkNumber:      string
  jobId:           string
  jobNumber:       string        // enriched from jobs join — Phase 6
  conductedById:   string
  conductedByName: string        // enriched from users join — Phase 6
  date:            string        // YYYY-MM-DD
  topic:           string
  summary:         string
  signoffRequired: boolean
  status:          'DRAFT' | 'COMPLETED'
  attendees: {
    id:              string
    workerId:        string
    workerName:      string
    workerType:      'EMPLOYEE' | 'SUBCONTRACTOR'
    acknowledgedAt?: string
  }[]
}

function mapApiTalk(raw: ApiToolboxTalk): MappedToolboxTalk {
  const conductedAt = raw.conductedAt ?? raw.conducted_at ?? new Date().toISOString()
  return {
    id:              raw.id,
    talkNumber:      `TBT-${raw.id.slice(-8).toUpperCase()}`,
    jobId:           raw.jobId      ?? raw.job_id      ?? '',
    jobNumber:       '',          // enriched from jobs join — Phase 6
    conductedById:   raw.conductedById ?? raw.conducted_by_id ?? '',
    conductedByName: '',          // enriched from users join — Phase 6
    date:            conductedAt.slice(0, 10),
    topic:           raw.topic  ?? 'Toolbox talk',
    summary:         raw.notes  ?? '',
    signoffRequired: true,
    status:          'COMPLETED',
    attendees: (raw.attendees ?? []).map(a => ({
      id:             a.id,
      workerId:       a.userId ?? a.user_id ?? a.id,
      workerName:     a.workerName ?? a.worker_name ?? 'Worker',
      workerType:     'EMPLOYEE' as const,
      acknowledgedAt: a.signedAt ?? a.signed_at ?? undefined,
    })),
  }
}

export async function fetchToolboxTalks(jobId?: string): Promise<MappedToolboxTalk[]> {
  const params = jobId ? `?jobId=${jobId}` : ''
  const { data } = await api.get<{ data: ApiToolboxTalk[] } | ApiToolboxTalk[]>(`/api/whs/toolbox${params}`)
  const raw: ApiToolboxTalk[] = Array.isArray(data) ? data : ((data as any).data ?? [])
  return raw.map(mapApiTalk)
}
